import { type RefObject, useEffect, useState } from 'react';
import { throttle } from '../helpers/helpers';

const CHECK_SCROLL_STATE_THROTTLE = 200;

type ScrollState = {
  isAtStart: boolean;
  isAtEnd: boolean;
};

type ScrollDirection = 'vertical' | 'horizontal' | 'both';

/**
 * Hook that tracks the common scroll states of an element.
 * It gets recalculated on scroll and resize events.
 */
export function useScrollState<T extends HTMLElement>(
  /** React ref to the DOM element to track scroll state */
  ref?: RefObject<T | null>,
  /**
   * Direction to track scroll state.
   *
   * @default 'both'
   */
  direction: ScrollDirection = 'both',
  /**
   * Threshold in pixels to determine how far from the true start/end to be considered as start/end.
   *
   * @default 0
   */
  threshold = 0,
): ScrollState {
  const [scrollState, setScrollState] = useState<ScrollState>({
    isAtStart: true,
    isAtEnd: true,
  });

  useEffect(() => {
    const element = ref?.current;
    if (!element) {
      return;
    }

    const checkScrollState = throttle(() => {
      const {
        scrollTop: floatingScrollTop,
        scrollLeft: floatingScrollLeft,
        scrollHeight,
        scrollWidth,
        clientHeight,
        clientWidth,
      } = element;

      // Standardize scrollTop and scrollLeft to be integers like the width/height properties
      const scrollTop = Math.ceil(floatingScrollTop);
      const scrollLeft = Math.ceil(floatingScrollLeft);

      const isAtStart = {
        vertical: scrollTop <= threshold,
        horizontal: scrollLeft <= threshold,
      };

      const isAtEnd = {
        vertical: scrollTop + clientHeight >= scrollHeight - threshold,
        horizontal: scrollLeft + clientWidth >= scrollWidth - threshold,
      };

      if (direction === 'both') {
        setScrollState({
          isAtStart: isAtStart.vertical && isAtStart.horizontal,
          isAtEnd: isAtEnd.vertical && isAtEnd.horizontal,
        });
      } else {
        setScrollState({
          isAtStart: isAtStart[direction],
          isAtEnd: isAtEnd[direction],
        });
      }
    }, CHECK_SCROLL_STATE_THROTTLE);

    checkScrollState();

    element.addEventListener('scroll', checkScrollState);

    const resizeObserver = new ResizeObserver(checkScrollState);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', checkScrollState);
      resizeObserver.disconnect();
    };
  }, [ref, threshold, direction]);

  return scrollState;
}
